import type {
  RestaurantSetting,
  ReservationServicesSettings,
  RewardDays,
  ShopSettingDeliveryCity,
  MenuPdfItem,
} from "@lemmonhq/types";

/**
 * Parse a boolean value from a RestaurantSetting
 */
export const parseBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (value === "true" || value === "1") return true;
  return false;
};

/**
 * Parse a number value from a RestaurantSetting
 */
export const parseNumber = (value: unknown, defaultValue: number): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
};

/**
 * Parse a string value from a RestaurantSetting
 */
export const parseString = (value: unknown, defaultValue = ""): string => {
  if (typeof value === "string") return value;
  return defaultValue;
};

/**
 * Parse a nullable string value from a RestaurantSetting
 */
export const parseNullableString = (value: unknown): string | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed.length === 0 ? null : trimmed;
};

/**
 * Parse delivery cities from a JSON string
 */
export const parseDeliveryCities = (
  value: unknown,
): ShopSettingDeliveryCity[] => {
  if (!value) return [];

  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (city): city is ShopSettingDeliveryCity =>
          typeof city === "object" &&
          city !== null &&
          typeof city.name === "string" &&
          typeof city.fee === "string" &&
          typeof city.minimum === "string",
      );
    }
  } catch (error) {
    console.error("Failed to parse delivery cities", error);
  }
  return [];
};

/**
 * Parse reservation services from a JSON string or object
 */
export const parseReservationServices = (
  value: unknown,
): ReservationServicesSettings => {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return {
        lunch: parseBoolean(parsed?.lunch),
        dinner: parseBoolean(parsed?.dinner),
      };
    } catch {
      return { lunch: true, dinner: true };
    }
  }
  if (typeof value === "object" && value !== null) {
    const obj = value as Record<string, unknown>;
    return {
      lunch: parseBoolean(obj.lunch),
      dinner: parseBoolean(obj.dinner),
    };
  }
  return { lunch: true, dinner: true };
};

/**
 * Parse reservation schedule from a JSON string or object
 */
export const parseReservationSchedule = (value: unknown): RewardDays => {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed as RewardDays;
      }
    } catch {
      return {};
    }
  }
  if (typeof value === "object" && value !== null) {
    return value as RewardDays;
  }
  return {};
};

/**
 * Returns true if the given value is a string URL using the http or https
 * protocol. Used to reject dangerous schemes (e.g. `javascript:`) before a
 * URL is persisted or rendered as a link.
 */
export const isHttpUrl = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * Parse menu PDFs from a JSON string or array
 */
export const parseMenuPdfs = (value: unknown): MenuPdfItem[] => {
  const parseArray = (parsed: unknown): MenuPdfItem[] => {
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item) => {
      if (
        typeof item !== "object" ||
        item === null ||
        typeof (item as MenuPdfItem).name !== "string" ||
        !isHttpUrl((item as MenuPdfItem).url)
      ) {
        return [];
      }

      const menuPdf = item as MenuPdfItem;
      return [
        {
          name: menuPdf.name,
          url: menuPdf.url,
          ...(typeof menuPdf.assetId === "string"
            ? { assetId: menuPdf.assetId }
            : {}),
        },
      ];
    });
  };

  if (typeof value === "string") {
    if (!value) return [];
    try {
      return parseArray(JSON.parse(value));
    } catch {
      return [];
    }
  }
  if (Array.isArray(value)) {
    return parseArray(value);
  }
  return [];
};

/**
 * Parse a JSON value from a RestaurantSetting, handling both objects and stringified JSON
 */
export const parseJsonValue = <T = unknown>(
  value: unknown,
  defaultValue: T,
): T => {
  // If already the expected type (array or object), return it
  if (value !== null && typeof value === "object") {
    return value as T;
  }

  // If it's a string, try to parse it
  if (typeof value === "string" && value.length > 0) {
    try {
      const parsed = JSON.parse(value);
      return parsed as T;
    } catch (error) {
      console.error("Failed to parse JSON value:", value, error);
      return defaultValue;
    }
  }

  // Return default for null, undefined, or empty string
  return defaultValue;
};

/**
 * Get a setting value by key from an array of settings
 */
export const getSettingValue = (
  settings: RestaurantSetting[],
  key: string,
): unknown => {
  const setting = settings.find((s) => s.key === key);
  return setting?.value;
};

/**
 * Get a boolean setting value
 */
export const getBooleanSetting = (
  settings: RestaurantSetting[],
  key: string,
  defaultValue = false,
): boolean => {
  const value = getSettingValue(settings, key);
  return value !== undefined ? parseBoolean(value) : defaultValue;
};

/**
 * Get a number setting value
 */
export const getNumberSetting = (
  settings: RestaurantSetting[],
  key: string,
  defaultValue: number,
): number => {
  const value = getSettingValue(settings, key);
  return value !== undefined ? parseNumber(value, defaultValue) : defaultValue;
};

/**
 * Get a string setting value
 */
export const getStringSetting = (
  settings: RestaurantSetting[],
  key: string,
  defaultValue = "",
): string => {
  const value = getSettingValue(settings, key);
  return value !== undefined ? parseString(value, defaultValue) : defaultValue;
};

/**
 * Get a nullable string setting value
 */
export const getNullableStringSetting = (
  settings: RestaurantSetting[],
  key: string,
): string | null => {
  const value = getSettingValue(settings, key);
  return value !== undefined ? parseNullableString(value) : null;
};

/**
 * Create a setting object for upsert
 */
export const createSetting = (
  key: string,
  value: unknown,
  valueType: "string" | "number" | "boolean" | "json" = "string",
): { key: string; value: string; value_type: string } => {
  let stringValue: string;

  if (value === null || value === undefined) {
    stringValue = "";
  } else if (typeof value === "object") {
    stringValue = JSON.stringify(value);
  } else {
    stringValue = String(value);
  }

  return {
    key,
    value: stringValue,
    value_type: valueType,
  };
};
